import { Component, EventEmitter, Input, Output, HostListener, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

/** these are the classes for the sorter icon */
export interface SorterComponentClasses {
  notSorted?: string;
  sortable?: string;
  sortAsc?: string;
  sortDesc?: string;
}

/** Can be false for not sortable, true for sortable, asc for sorted ascending or desc for sorted secending */
export type SorterComponentState = boolean | 'asc' | 'desc';

export interface SorterComponentChangeEvent {
  state: SorterComponentState;
  tag: any;
}

/**
 * A sorter control that can be used to toggle sorting by a reference to the component.
 * @example
 * ```
 * <table tccSorterHost> <!-- sorter host is optional, but coordinates sorters
 *   <tr>
 *     <th>
 *       <tcc-sorter [cssClasses]="{ 'sortAsc': 'text-primary fas fa-sort-up'">
 *         Optional Inner Content
 *       </tcc-sorter>
 *     </th>
 *   </tr>
 * </table>
 * ```
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tcc-sorter',
  template: `<i [ngClass]="getIconClass()"></i><ng-content></ng-content>`
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class SorterComponent {
  /** Default sorter css classes */
  static readonly defaultOptions: Required<SorterComponentClasses> = {
    sortable: 'text-muted fas fa-sort ',
    sortAsc: 'fas fa-sort-up',
    sortDesc: 'fas fa-sort-down',
    notSorted: ''
  };

  private _cssClasses: Required<SorterComponentClasses> = { ...SorterComponent.defaultOptions };
  private _sorterState: SorterComponentState = false;

  /** ServerComponentClasses options.  If a value is not provided the value from defaultOptions is used */
  @Input()
  set cssClasses(value: SorterComponentClasses) {
    this._cssClasses = { ...SorterComponent.defaultOptions, ...value };
  }
  get cssClasses() { return this._cssClasses; }

  /** the next direction to sort when sorterState is true (sortable but not sorted).  Defaults to 'asc' */
  @Input()
  defaultSortDirection: 'asc' | 'desc' = 'asc';

  /** the state of the sorter, false: not sortable, true: sortable, but not sorted, 'asc': sorted ascending, 'desc' sorted descending. */
  @Input()
  set sorterState(value: SorterComponentState) {
    this.updateSorterState(value);
  }
  get sorterState() { return this._sorterState; }

  /** Emits event when the state of the sorter changes. */
  @Output()
  readonly sorterStateChange = new EventEmitter<SorterComponentChangeEvent>()

  /** any value that is sent along with a sorter change event */
  @Input()
  sorterTag: any;

  /** If true, toggling goes from defaultSortDir to altSortDir to true, instead of just between defautlSortDir to altSortDir. */
  @Input()
  triStateToggle = false;

  constructor(private cd: ChangeDetectorRef) { }

  getIconClass() {
    switch (this._sorterState) {
      case true: return this._cssClasses.sortable;
      case 'asc': return this._cssClasses.sortAsc;
      case 'desc': return this._cssClasses.sortDesc;
      default: return this._cssClasses.notSorted;
    }
  }

  @HostListener('click')
  handleClick() {
    this.toggleSort();
  }

  /**
   * Will set the sort direction to the next direction based on the current state.
   */
  toggleSort() {
    if (this._sorterState) {
      const altSortDirection = (this.defaultSortDirection === 'desc') ? 'asc' : 'desc';
      const sortDirection = (this._sorterState === this.defaultSortDirection)
        ? altSortDirection
        : (this.triStateToggle && this._sorterState === altSortDirection) ? true : this.defaultSortDirection;
      this.updateSorterState(sortDirection);
    }
  }

  private updateSorterState(value: SorterComponentState) {
    if (value !== true && value !== 'asc' && value !== 'desc') {
      // normalize value
      value = false;
    }
    if (value !== this._sorterState) {
      this._sorterState = value;
      this.sorterStateChange.emit({ state: this._sorterState, tag: this.sorterTag });
      this.cd.detectChanges();
    }
  }
}
